import React from 'react';
import CodeBlock from '../components/CodeBlock';

const Webhooks = () => {
  return (
    <div className="page">
      <div className="page-header">
        <h1>🔔 Webhooks sortants</h1>
        <p>
          Recevez des notifications HTTP en temps réel depuis Whakup vers votre serveur quand
          des événements se produisent (message reçu, contact créé, campagne terminée, etc.).
        </p>
      </div>

      <div className="section">
        <h2>Vue d'ensemble</h2>
        <p>
          Quand un événement se produit dans Whakup, on POST un payload JSON signé HMAC SHA-256
          vers l'URL que vous avez configurée. Si votre endpoint répond <strong>HTTP 2xx</strong>,
          la livraison est marquée comme réussie. Sinon, on retente automatiquement (3 tentatives
          avec backoff exponentiel), puis on désactive le webhook après 5 échecs consécutifs.
        </p>
      </div>

      <div className="section">
        <h2>Événements supportés</h2>
        <table className="params-table">
          <thead><tr><th>Event</th><th>Déclenchement</th></tr></thead>
          <tbody>
            <tr><td><code>contact.created</code></td><td>Nouveau contact créé</td></tr>
            <tr><td><code>contact.updated</code></td><td>Contact modifié</td></tr>
            <tr><td><code>contact.deleted</code></td><td>Contact supprimé</td></tr>
            <tr><td><code>contact.blacklisted</code></td><td>Contact passé en blacklist</td></tr>
            <tr><td><code>message.received</code></td><td>Le contact répond</td></tr>
            <tr><td><code>message.sent</code></td><td>Confirmation d'envoi côté Meta</td></tr>
            <tr><td><code>message.delivered</code></td><td>Message remis à l'appareil</td></tr>
            <tr><td><code>message.read</code></td><td>Le contact a lu le message</td></tr>
            <tr><td><code>message.failed</code></td><td>Échec d'envoi (avec error code)</td></tr>
            <tr><td><code>campaign.completed</code></td><td>Campagne terminée</td></tr>
            <tr><td><code>template.approved</code></td><td>Template validé par Meta</td></tr>
            <tr><td><code>template.rejected</code></td><td>Template refusé par Meta</td></tr>
          </tbody>
        </table>
      </div>

      <div className="section">
        <h2>Format du payload</h2>
        <p>Tous les webhooks suivent la même structure :</p>
        <CodeBlock language="json" code={`{
  "event": "message.received",
  "workspace_id": 42,
  "data": {
    "contact": {
      "id": 1234,
      "phone": "+33612345678",
      "firstName": "Jean",
      "lastName": "Dupont"
    },
    "message": {
      "type": "text",
      "content": "Bonjour",
      "buttonText": null,
      "buttonPayload": null
    }
  },
  "timestamp": "2026-05-04T14:30:00+00:00"
}`} />
      </div>

      <div className="section">
        <h2>Headers HTTP</h2>
        <table className="params-table">
          <thead><tr><th>Header</th><th>Description</th></tr></thead>
          <tbody>
            <tr><td><code>X-Whakup-Signature</code></td><td><code>sha256=&lt;hex&gt;</code> — signature HMAC du body</td></tr>
            <tr><td><code>X-Whakup-Event</code></td><td>Type d'événement (ex: <code>message.received</code>)</td></tr>
            <tr><td><code>X-Whakup-Timestamp</code></td><td>Timestamp Unix de l'envoi</td></tr>
            <tr><td><code>User-Agent</code></td><td><code>Whakup-Webhooks/1.0</code></td></tr>
            <tr><td><code>Content-Type</code></td><td><code>application/json</code></td></tr>
          </tbody>
        </table>
      </div>

      <div className="section">
        <h2>Vérification de signature</h2>
        <p>
          <strong>Toujours vérifier la signature</strong> avant de traiter un webhook — sinon n'importe qui
          peut faire un POST sur votre endpoint avec un payload falsifié.
        </p>

        <h4>Node.js (Express)</h4>
        <CodeBlock language="javascript" code={`const crypto = require('crypto');

app.post('/webhook/whakup', (req, res) => {
  const signature = req.headers['x-whakup-signature'];
  const body = JSON.stringify(req.body); // ou req.rawBody si dispo
  const expected = 'sha256=' + crypto
    .createHmac('sha256', process.env.WHAKUP_WEBHOOK_SECRET)
    .update(body)
    .digest('hex');

  if (!crypto.timingSafeEqual(Buffer.from(signature), Buffer.from(expected))) {
    return res.status(401).send('Invalid signature');
  }

  // Traiter l'événement
  console.log(req.body.event, req.body.data);
  res.status(200).send('OK');
});`} />

        <h4>Python (Flask)</h4>
        <CodeBlock language="python" code={`import hmac
import hashlib
from flask import request, abort

@app.route('/webhook/whakup', methods=['POST'])
def whakup_webhook():
    signature = request.headers.get('X-Whakup-Signature', '')
    body = request.get_data()
    expected = 'sha256=' + hmac.new(
        WHAKUP_WEBHOOK_SECRET.encode(),
        body,
        hashlib.sha256
    ).hexdigest()
    if not hmac.compare_digest(signature, expected):
        abort(401)

    payload = request.get_json()
    # … traiter l'événement
    return 'OK', 200`} />

        <h4>PHP</h4>
        <CodeBlock language="php" code={`$signature = $_SERVER['HTTP_X_WHAKUP_SIGNATURE'] ?? '';
$body = file_get_contents('php://input');
$expected = 'sha256=' . hash_hmac('sha256', $body, $WHAKUP_WEBHOOK_SECRET);

if (!hash_equals($expected, $signature)) {
    http_response_code(401);
    exit('Invalid signature');
}

$payload = json_decode($body, true);
// …`} />
      </div>

      <div className="section">
        <h2>Gestion des webhooks via API</h2>

        <div className="endpoint-card">
          <div className="endpoint-header">
            <span className="method-badge method-get">GET</span>
            <span className="endpoint-path">/api/v1/webhooks/events</span>
          </div>
          <p>Liste des événements supportés (utile pour build une UI dynamique).</p>
        </div>

        <div className="endpoint-card">
          <div className="endpoint-header">
            <span className="method-badge method-post">POST</span>
            <span className="endpoint-path">/api/v1/webhooks</span>
          </div>
          <h4>Body</h4>
          <CodeBlock language="json" code={`{
  "url": "https://votre-serveur.com/webhook/whakup",
  "events": ["message.received", "contact.created"],
  "description": "Sync vers HubSpot via n8n"
}`} />
          <h4>Réponse 201</h4>
          <CodeBlock language="json" code={`{
  "id": 12,
  "url": "https://votre-serveur.com/webhook/whakup",
  "events": ["message.received", "contact.created"],
  "secret": "abc123def456...",
  "isActive": true,
  "createdAt": "2026-05-04 14:30:00"
}`} />
          <div className="alert alert-info" style={{ marginTop: '1rem' }}>
            ⚠️ <strong>Le champ <code>secret</code> n'est retourné qu'une seule fois</strong> — conservez-le précieusement,
            il est nécessaire pour vérifier la signature des webhooks reçus.
          </div>
        </div>

        <div className="endpoint-card">
          <div className="endpoint-header">
            <span className="method-badge method-get">GET</span>
            <span className="endpoint-path">/api/v1/webhooks</span>
          </div>
          <p>Liste les webhooks du workspace courant (le secret n'est pas inclus).</p>
        </div>

        <div className="endpoint-card">
          <div className="endpoint-header">
            <span className="method-badge method-put">PUT</span>
            <span className="endpoint-path">/api/v1/webhooks/{'{id}'}</span>
          </div>
          <p>Met à jour <code>url</code>, <code>events</code>, <code>description</code>, ou <code>isActive</code>.</p>
        </div>

        <div className="endpoint-card">
          <div className="endpoint-header">
            <span className="method-badge method-delete">DELETE</span>
            <span className="endpoint-path">/api/v1/webhooks/{'{id}'}</span>
          </div>
        </div>

        <div className="endpoint-card">
          <div className="endpoint-header">
            <span className="method-badge method-post">POST</span>
            <span className="endpoint-path">/api/v1/webhooks/{'{id}'}/test</span>
          </div>
          <p>Envoie un payload <code>webhook.test</code> à votre URL pour vérifier votre intégration.</p>
        </div>

        <div className="endpoint-card">
          <div className="endpoint-header">
            <span className="method-badge method-get">GET</span>
            <span className="endpoint-path">/api/v1/webhooks/{'{id}'}/deliveries</span>
          </div>
          <p>Historique des 20 dernières tentatives (status, response code, error message). Utile pour debug.</p>
        </div>
      </div>

      <div className="section">
        <h2>Désactivation automatique</h2>
        <p>
          Si votre endpoint répond avec un code &gt;= 400 ou time-out <strong>5 fois consécutives</strong>,
          le webhook est <strong>automatiquement désactivé</strong> (<code>isActive: false</code>).
          Vous devez le réactiver manuellement via <code>PUT /api/v1/webhooks/{'{id}'}</code> avec
          <code>{`{ "isActive": true }`}</code>.
        </p>
      </div>

      <div className="section">
        <h2>Scope requis</h2>
        <p><code>webhooks:manage</code> pour toutes les opérations CRUD.</p>
      </div>
    </div>
  );
};

export default Webhooks;
